/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

import Navbar from '../Navbar';
import Header from '../Header';
import Loader from '../Loader';
import baseUrl from '../BaseUrl';

import FilterBar from './FilterBar';
import ActivityTable from './ActivityTable';
import DailyTrendLine from './charts/DailyTrendLine';
import StatusDonut from './charts/StatusDonut';
import EventTypeBar from './charts/EventTypeBar';
import { fetchList, fetchAggregates } from './api';
import './webhook-activity.css';

const iso = (d) => d.toISOString().slice(0, 10);
const defaultEnd = () => iso(new Date());
const defaultStart = () => iso(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

const DEFAULT_FILTER = () => ({
  startDate: defaultStart(),
  endDate: defaultEnd(),
  platform: [],
  eventTypes: [],
  status: ['Active', 'Cancelled', 'Expired'],
  page: 1,
  limit: 25,
});

const WebhookActivityDashboard = () => {
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [filter, setFilter] = useState(DEFAULT_FILTER());
  const [listData, setListData] = useState({ rows: [], page: 1, totalPages: 0, totalItems: 0 });
  const [aggregates, setAggregates] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    refreshToken();
  }, []);

  useEffect(() => {
    setName('Pauser');
  }, []);

  const refreshToken = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/api/token`);
      const t = response.data.accessToken;
      setToken(t);
      setExpire(jwtDecode(t).exp);
    } catch (error) {
      if (error.response) {
        navigate('/');
        window.location.reload();
      }
    }
  };

  const ensureToken = useCallback(async () => {
    if (!token || expire * 1000 < Date.now()) {
      try {
        const response = await axios.get(`${baseUrl}/admin/api/token`);
        const t = response.data.accessToken;
        setToken(t);
        setExpire(jwtDecode(t).exp);
        return t;
      } catch {
        return null;
      }
    }
    return token;
  }, [token, expire]);

  const load = useCallback(async (f) => {
    setPopupOpen(true);
    try {
      const t = await ensureToken();
      if (!t) return;
      const [listResp, aggResp] = await Promise.all([
        fetchList(f, t),
        fetchAggregates(f, t),
      ]);
      if (listResp?.success) setListData(listResp.data);
      if (aggResp?.success) setAggregates(aggResp.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('webhook-activity load failed', err);
    } finally {
      setPopupOpen(false);
    }
  }, [ensureToken]);

  useEffect(() => {
    if (token) load(filter);
  }, [token]);

  const handleApply = (partial) => {
    const next = { ...filter, ...partial, page: 1 };
    setFilter(next);
    load(next);
  };

  const handlePageChange = (nextPage) => {
    const next = { ...filter, page: nextPage };
    setFilter(next);
    load(next);
  };

  const summary = aggregates?.summary;

  const platformLabel = (v) => (v === 1 ? 'Android' : v === 2 ? 'iOS' : String(v));
  const activeChips = [
    { key: 'range', label: `${filter.startDate} \u2192 ${filter.endDate}` },
    ...(filter.platform || []).map((p) => ({ key: `p-${p}`, label: `Platform: ${platformLabel(p)}` })),
    ...(filter.status || []).map((s) => ({ key: `s-${s}`, label: `Status: ${s}` })),
  ];

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name={name} />
        {popupOpen && <Loader />}
        <div className="content-page">
          <div className="row">
            <div className="col-6">
              <div className="left-title">
                <h1>Webhook Activity</h1>
              </div>
            </div>
          </div>

          <div className="row numberboxes">
            {/* TODO: Re-enable "Total Webhooks" card when the metric is reintroduced.
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Total Webhooks</h3>
                <p>{summary?.totalWebhooks ?? 0}</p>
              </div>
            </div>
            */}
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Android Users</h3>
                <p>{summary?.byPlatform?.android ?? 0}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>iOS Users</h3>
                <p>{summary?.byPlatform?.ios ?? 0}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Unique Users</h3>
                <p>{summary?.uniqueUsers ?? 0}</p>
              </div>
            </div>
          </div>

          <FilterBar onApply={handleApply} />

          <div className="wa-active-filters" aria-label="Active filters">
            <span className="wa-active-filters-label">Showing:</span>
            {activeChips.map((c) => (
              <span key={c.key} className="wa-chip">{c.label}</span>
            ))}
            <span className="wa-active-filters-count">
              {listData.totalItems} result{listData.totalItems === 1 ? '' : 's'}
            </span>
          </div>

          <div className="wa-charts-grid">
            <div className="wa-chart-card wa-chart-card--wide">
              <h3 className="wa-chart-title">Daily Activity</h3>
              <DailyTrendLine data={aggregates?.dailyTrend} />
            </div>
            <div className="wa-chart-card">
              <h3 className="wa-chart-title">Status Distribution</h3>
              <StatusDonut data={aggregates?.statusDistribution} />
            </div>
            <div className="wa-chart-card wa-chart-card--wide">
              <h3 className="wa-chart-title">Event Type Breakdown</h3>
              <EventTypeBar data={aggregates?.eventTypeBreakdown} />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <ActivityTable
                rows={listData.rows}
                page={listData.page}
                totalPages={listData.totalPages}
                totalItems={listData.totalItems}
                limit={filter.limit}
                onPageChange={handlePageChange}
                loading={popupOpen}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookActivityDashboard;
